import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'helpers/Analytics';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  HelpBlock,
  Row,
} from 'react-bootstrap';
import Panel from 'components/Panel/Panel';
import styles from './EditProject.module.scss';
import RolesSelector from 'components/Shared/Selectors/RolesSelector';
import {
  updateProject,
  loadCredits,
  loadProject,
  updateCredits,
} from 'redux/modules/textProjects';
import useAuth from 'hooks/useAuth';
import Loader from 'components/Loader/Loader';
import MarkdownSection from 'components/TextProjects/MarkdownSection';
import ProjectMediaPanel from 'components/TextProjects/ProjectMediaPanel';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import { setSuccessMessage } from 'redux/modules/successMessage';

const EditProject = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { hash } = useParams();

  useEffect(() => {
    dispatch(loadProject('c' + hash));
    dispatch(loadCredits('c' + hash));
  }, []);

  const projectSelect = (state) => state.textProjects;
  const projectState = useSelector(projectSelect);

  const {
    loadingProject,
    loadingCredits,
    updatingProject,
    updatingCredits,
    project,
    credits,
  } = projectState;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaPayload, setMediaPayload] = useState({
    file: null,
    thumbnail: null,
    crop: null,
    remove_media: false,
    remove_thumbnail: false,
    uploading: false,
  });

  useEffect(() => {
    if (!loadingProject && !loadingCredits) {
      setLoading(false);
      setTitle(project?.title);
      setDescription(project?.description);
      const creditedRoles = credits?.[0]?.roles?.map((role) => role.name) || [];
      setSelectedRoles(creditedRoles);
    } else {
      setLoading(true);
    }
  }, [project, credits, loadingProject, loadingCredits]);

  const handleRolesChange = (roles) => {
    setSelectedRoles(roles);
  };

  const isProcessing = project?.state === 'processing';
  const busy = updatingProject || updatingCredits || mediaPayload.uploading;

  const buildUpdateData = () => {
    const data = {
      title,
      description,
    };

    if (mediaPayload.remove_media) {
      data.remove_media = true;
    } else if (mediaPayload.file) {
      data.file = mediaPayload.file;
    }

    if (mediaPayload.remove_thumbnail) {
      data.remove_thumbnail = true;
    } else if (mediaPayload.thumbnail) {
      data.thumbnail = mediaPayload.thumbnail;
      if (mediaPayload.crop) {
        data.crop = mediaPayload.crop;
      }
    }

    return data;
  };

  const handleUpdateProject = () => {
    if (mediaPayload.uploading) {
      return;
    }

    if (!selectedRoles.length) {
      setErrors({
        roles: ['Please fill in this field with at least 1 role selected'],
      });
      return;
    }

    setErrors({});
    dispatch(updateProject(project.id, buildUpdateData()))
      .then((updated) => {
        const goToProject = (creditsFailed = false) => {
          trackEvent('freelancer-updates-project');
          dispatch(
            setSuccessMessage(
              creditsFailed
                ? 'Project updated. Roles could not be saved — try again from this page.'
                : 'Project successfully updated'
            )
          );
          window.location.href =
            (updated && updated.links && updated.links.main_relative) ||
            project.links.main_relative;
        };

        return dispatch(
          updateCredits(
            project.id,
            user.id,
            selectedRoles.map((role) => role?.value || role)
          )
        )
          .then(() => goToProject(false))
          .catch((error) => {
            console.error('Error updating credits:', error);
            goToProject(true);
          });
      })
      .catch((error) => {
        console.error('Error updating project:', error);
        setErrors(error.errors || {});
      });
  };

  return (
    <div className="mt++ lato-regular">
      <Grid>
        <Row>
          <Col lg={8}>
            <Breadcrumbs className={styles.breadcrumbs}>
              <Link to="/projects/manage">Manage Projects</Link>
              Edit Project
            </Breadcrumbs>
            <Panel className={styles.container}>
              {loading ? (
                <Loader loading />
              ) : (
                <>
                  <p className={styles.title}>Editing {project?.title}</p>
                  {isProcessing && (
                    <div className={styles.processingBanner} role="status">
                      Your media is still processing. Streams and thumbnails may
                      not be ready yet. You can still edit the title,
                      description, and roles.
                    </div>
                  )}
                  <FormGroup controlId="project-title">
                    <ControlLabel className="fs-15">Project Title</ControlLabel>
                    <FormControl
                      type="text"
                      value={title || ''}
                      placeholder="Enter the title of your project here"
                      onChange={(event) => setTitle(event.target.value)}
                      maxLength="140"
                      style={{ fontSize: '14px' }}
                      disabled={busy}
                    />
                    {errors?.title && (
                      <HelpBlock className={styles.error}>
                        {errors?.title[0]}
                      </HelpBlock>
                    )}
                  </FormGroup>
                  <ProjectMediaPanel
                    mode="edit"
                    project={project}
                    disabled={busy}
                    processing={isProcessing}
                    onChange={setMediaPayload}
                  />
                  {errors?.file && (
                    <HelpBlock className={styles.error}>
                      {errors.file[0]}
                    </HelpBlock>
                  )}
                  <FormGroup controlId="project-description">
                    <ControlLabel className="fs-15">
                      Project Description
                    </ControlLabel>
                    <FormControl
                      componentClass="textarea"
                      value={description || ''}
                      placeholder="Enter the description of your project here. Use the cheat sheet on this page to see how you can make use of the Markdown support and various services embeds"
                      onChange={(event) => setDescription(event.target.value)}
                      style={{ fontSize: '14px' }}
                      rows="10"
                      disabled={busy}
                    />
                    {errors?.description && (
                      <HelpBlock className={styles.error}>
                        {errors?.description[0]}
                      </HelpBlock>
                    )}
                  </FormGroup>
                  <FormGroup controlId="project-credits">
                    <ControlLabel className="fs-15">
                      Your project role(s)
                    </ControlLabel>
                    <RolesSelector
                      key={JSON.stringify(
                        (credits?.[0]?.roles || []).map((role) => role.name)
                      )}
                      maxValues={5}
                      style={{ width: '100%' }}
                      existing={selectedRoles.map(
                        (role) => role?.value || role
                      )}
                      onSelect={handleRolesChange}
                      disabled={busy}
                    />
                    {errors.roles && (
                      <HelpBlock className={styles.error}>
                        Please fill in this field with at least 1 role selected
                      </HelpBlock>
                    )}
                  </FormGroup>
                  <div className={styles.buttonContainer}>
                    <Button
                      bsStyle="default"
                      href={project?.links?.main_relative}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                    <Button
                      bsStyle="primary"
                      onClick={handleUpdateProject}
                      disabled={busy}
                    >
                      {mediaPayload.uploading
                        ? 'Uploading media…'
                        : busy
                        ? 'Saving…'
                        : 'Save Changes'}
                    </Button>
                  </div>
                </>
              )}
            </Panel>
          </Col>
          <Col lg={4}>
            <MarkdownSection />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default EditProject;
